import { cn } from '@avanta/shared/utils/cn';

/**
 * Shared styles for dropdown-like components (Select, Combobox, CountrySelect…).
 * Each constant is atomic — compose them per component adding geometry and animations as needed.
 */

export const dropdownSvgReset = '[&_svg]:pointer-events-none [&_svg]:shrink-0';

export const dropdownFormWrapper = cn(
  'flex flex-col gap-2 group/form',
  'data-[variant=filled]:[--icon-color:var(--color-teal-700)] data-[variant=filled]:[--icon-color-muted:var(--color-teal-600)]',
  'data-[variant=invalid]:[--icon-color:var(--color-red-500)] data-[variant=invalid]:[--icon-color-muted:var(--color-red-400)]'
);

/** Trigger appearance without disabled styles — use when disabled behavior differs (e.g. CountrySelect). */
export const dropdownTriggerAppearance = cn(
  'group/trigger flex h-10 items-center justify-between gap-2',
  'rounded-lg border bg-gray-100',
  'font-normal text-sm whitespace-nowrap outline-none',
  'transition-[color,box-shadow]',
  'data-[has-value=false]:border-teal-300',
  'data-[has-value=true]:border-teal-500 data-[has-value=true]:bg-mint-100',
  dropdownSvgReset
);

/** Default `disabled` behavior used by Select and Combobox. */
export const dropdownTriggerDisabled = 'disabled:cursor-not-allowed disabled:opacity-50';

export const dropdownTriggerBase = cn(dropdownTriggerAppearance, dropdownTriggerDisabled);

export const dropdownTriggerInvalid = cn(
  'group-data-[variant=invalid]/form:border-red-400 group-data-[variant=invalid]/form:bg-red-100',
  'group-data-[variant=invalid]/form:data-[has-value=true]:border-red-400 group-data-[variant=invalid]/form:data-[has-value=true]:bg-red-100'
);

export const dropdownContentBase = cn(
  'relative z-50 flex flex-col max-h-75 min-w-32',
  'overflow-x-hidden overflow-y-auto',
  'rounded-lg border border-plum-100 bg-white shadow-select-sm',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
  'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95'
);

export const dropdownItemBase = cn(
  'relative flex h-10 w-full cursor-default items-center justify-between gap-2',
  'px-4 py-2 outline-hidden select-none',
  'text-sm leading-4 text-denim-500',
  dropdownSvgReset
);

/** Item for cmdk (CommandItem): selected state via `data-[selected=true]`. */
export const dropdownCommandItem = cn(
  dropdownItemBase,
  'rounded-none',
  'data-[selected=true]:bg-plum-100 data-[selected=true]:text-denim-900'
);

/** Item for Radix (Select.Item): selected state via `data-[state=selected]`. */
export const dropdownSelectItem = cn(
  dropdownItemBase,
  'hover:bg-plum-100 hover:text-denim-900',
  'focus:font-medium',
  'data-[state=selected]:bg-plum-100 data-[state=selected]:text-denim-900',
  'data-[disabled]:pointer-events-none data-[disabled]:text-gray-300'
);
